<template>
    <div v-if="visible && selectedId" class="pb2-toolbar" :style="position">
        <button
            v-tooltip="{ message: t('drag_hint'), position: 'top' }"
            type="button"
            class="pb2-toolbar__button pb2-toolbar__button--handle"
            :aria-label="t('drag_hint')"
            @pointerdown="startDrag"
        >
            <Icon icon="phosphor/regular/dots-six-vertical" class="size-3.5" />
        </button>
        <button
            v-tooltip="{ message: t('move_up'), position: 'top' }"
            type="button"
            class="pb2-toolbar__button"
            :aria-label="t('move_up')"
            @click="store.moveBlock(selectedId, -1)"
        >
            <Icon icon="phosphor/regular/arrow-up" class="size-3.5" />
        </button>
        <button
            v-tooltip="{ message: t('move_down'), position: 'top' }"
            type="button"
            class="pb2-toolbar__button"
            :aria-label="t('move_down')"
            @click="store.moveBlock(selectedId, 1)"
        >
            <Icon icon="phosphor/regular/arrow-down" class="size-3.5" />
        </button>
        <button
            v-tooltip="{ message: t('duplicate'), position: 'top' }"
            type="button"
            class="pb2-toolbar__button"
            :aria-label="t('duplicate')"
            @click="store.duplicateBlock(selectedId)"
        >
            <Icon icon="phosphor/regular/copy" class="size-3.5" />
        </button>
        <button
            v-if="wrapEligible"
            v-tooltip="{ message: t('wrap_in_section'), position: 'top' }"
            type="button"
            class="pb2-toolbar__button"
            :aria-label="t('wrap_in_section')"
            @click="store.wrapInSection([selectedId])"
        >
            <Icon icon="phosphor/regular/frame-corners" class="size-3.5" />
        </button>
        <button
            v-tooltip="{ message: t('delete'), position: 'top' }"
            type="button"
            class="pb2-toolbar__button pb2-toolbar__button--danger"
            :aria-label="t('delete')"
            @click="store.removeBlock(selectedId)"
        >
            <Icon icon="phosphor/regular/trash" class="size-3.5" />
        </button>
        <div v-if="contextHint.visible.value" class="pb2-toolbar__hint">
            <HintChip :text="t('hint_context_menu')" @dismiss="contextHint.dismiss()" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from 'vue';
import HintChip from '@src/Common/Builder/HintChip.vue';
import { useBuilderT } from '@src/Common/Builder/host';
import { canWrapInSection } from '@src/Common/Builder/nestingRules';
import { useBuilderStore } from '@src/Common/Builder/useBuilderStore';
import { useDragMachine } from '@src/Common/Builder/useDragMachine';
import { useOneTimeHint } from '@src/Common/Builder/useOneTimeHint';

const t = useBuilderT();

const store = useBuilderStore();
const machine = useDragMachine();

// The hint has served its purpose the moment the user opens any context menu.
const contextHint = useOneTimeHint('context-menu');
const dismissHintOnMenu = (): void => contextHint.dismiss();

onMounted(() => window.addEventListener('contextmenu', dismissHintOnMenu));
onBeforeUnmount(() => window.removeEventListener('contextmenu', dismissHintOnMenu));

const selectedId = computed(() => store.selectedId.value);

const wrapEligible = computed(
    () => selectedId.value !== null && canWrapInSection(store.content.value, [selectedId.value]),
);

const startDrag = (event: PointerEvent): void => {
    const id = selectedId.value;

    if (id === null) {
        return;
    }

    machine.beginPointerDrag(event, { kind: 'move', blockId: id }, store.labels.value[id] ?? '');
};
const rect = computed(() => (selectedId.value ? (store.rects.value[selectedId.value] ?? null) : null));

// hidden on multi-select — single-block arrows would be ambiguous
const visible = computed(
    () => rect.value !== null && store.mode.value === 'edit' && store.selectedIds.value.length === 1,
);

/** Positioned inside the canvas viewport from bridge-reported rects.
 *  Rects are unscaled iframe coordinates — multiply by the canvas scale. */
const position = computed(() => {
    if (!rect.value) {
        return {};
    }

    const scale = store.canvasScale.value || 1;

    return {
        top: `${Math.max(rect.value.top * scale, 0)}px`,
        left: `${Math.max((rect.value.left + rect.value.width) * scale - 4, 0)}px`,
    };
});
</script>

<style scoped>
.pb2-toolbar {
    position: absolute;
    display: flex;
    gap: 0;
    padding: 2px;
    transform: translate(-100%, -50%);
    background: var(--pb2-surface-raised, #fff);
    border: 1px solid var(--pb2-border, #e4e4e7);
    border-radius: var(--pb2-radius, 6px);
    box-shadow: var(--pb2-panel-shadow, var(--pb2-shadow, 0 8px 28px rgb(0 0 0 / 0.2)));
    z-index: 10;
    animation: pb2-toolbar-in 120ms ease-out;
}

@media (prefers-reduced-motion: reduce) {
    .pb2-toolbar {
        animation: none;
    }
}

@keyframes pb2-toolbar-in {
    from {
        opacity: 0;
        transform: translate(-100%, calc(-50% + 2px));
    }
}

.pb2-toolbar__button {
    display: grid;
    place-items: center;
    width: 1.5rem;
    height: 1.5rem;
    color: var(--pb2-muted, #a1a1aa);
    background: transparent;
    border: 0;
    border-radius: 4px;
    cursor: pointer;
    transition: color 140ms ease;
}

.pb2-toolbar__button:hover {
    color: var(--pb2-text, #3f3f46);
}

.pb2-toolbar__button:active {
    transform: translateY(0.5px);
}

.pb2-toolbar__button:focus-visible {
    outline: 2px solid var(--pb2-accent, #2563eb);
    outline-offset: 1px;
}

.pb2-toolbar__button--danger:hover {
    color: var(--pb2-danger, #dc2626);
}

.pb2-toolbar__button--handle {
    cursor: grab;
    touch-action: none;
}

.pb2-toolbar__hint {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    width: max-content;
    max-width: 240px;
    background: var(--pb2-surface-raised, #fff);
    border: 1px solid var(--pb2-border, #e4e4e7);
    border-radius: 4px;
    box-shadow: var(--pb2-panel-shadow, var(--pb2-shadow, 0 8px 28px rgb(0 0 0 / 0.2)));
}

</style>
